'use client';

/**
 * PwaUpdatePrompt — detects when a new Service Worker version is available
 * and shows a "Nueva versión disponible — Actualizar" prompt (like a
 * native app update).
 *
 * Sprint 134 (Founder directive 2026-09-19): "cuando la app (PWA) esté
 * desactualizada, pida actualizarla como una app nativa, y se le borre
 * el cache y le actualice la App"
 *
 * How it works:
 *   1. On mount, registers a `controllerchange` listener on
 *      `navigator.serviceWorker`.
 *   2. When the SW detects a new version (via `skipWaiting()`), the
 *      `controllerchange` event fires.
 *   3. This component shows a toast/dialog: "Nueva versión disponible"
 *      with an "Actualizar" button.
 *   4. When the user clicks "Actualizar":
 *      a. Send `{ type: 'CLEAR_CACHE' }` to the SW → SW deletes ALL
 *         caches + navigates all clients to their current URL (force
 *         reload).
 *      b. `window.location.reload()` as a fallback.
 *
 * The prompt is non-blocking (toast, not modal) — the user can continue
 * using the app + update when convenient. If the user ignores it, the
 * new SW will activate on next page load anyway (skipWaiting is already
 * called at top-level in sw.ts).
 *
 * Anti-P-263: NO internal refs in UI strings.
 * Anti-P-041: 0 `as any` casts.
 */

import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

export function PwaUpdatePrompt() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }

    let refreshing = false;

    // Listen for controller change → new SW took control
    const handleControllerChange = () => {
      if (refreshing) return;
      refreshing = true;
      // The new SW is now active. The activate event already cleared
      // all caches + sent CACHE_CLEARED_RELOAD to all clients.
      // Force a reload to ensure fresh content.
      window.location.reload();
    };

    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

    // PR fix (Founder directive 2026-11-03): listen for CACHE_CLEARED_RELOAD
    // message from the SW activate event. When the SW activates after a deploy,
    // it clears ALL caches + sends this message to all open tabs → auto-reload.
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'CACHE_CLEARED_RELOAD') {
        if (!refreshing) {
          refreshing = true;
          window.location.reload();
        }
      }
    };
    navigator.serviceWorker.addEventListener('message', handleMessage);

    // Check for waiting SW on mount + on page focus
    const checkForUpdate = async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (!reg) return;
        // If there's a waiting SW, a new version is available
        if (reg.waiting) {
          setUpdateAvailable(true);
        }
        // Listen for new SW installing → waiting
        reg.addEventListener('updatefound', () => {
          if (reg.installing) {
            reg.installing.addEventListener('statechange', () => {
              if (reg.waiting) {
                // New version is waiting
                setUpdateAvailable(true);
              }
            });
          }
        });
      } catch {
        // Silently ignore — SW not supported or not registered
      }
    };

    // P-370 fix: add .catch() to the async checkForUpdate() call — without
    // it, any rejection that escapes the try/catch (e.g., if the catch
    // block itself has a bug, or if navigator.serviceWorker throws in a
    // non-standard environment) surfaces as "Uncaught (in promise)
    // undefined" on every page load. The update check is best-effort.
    void checkForUpdate().catch(() => {
      // Silently ignore — SW not supported or not registered
    });

    // Also check on page focus (user returns to the app)
    const handleFocus = () => void checkForUpdate().catch(() => {});
    window.addEventListener('focus', handleFocus);

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      window.removeEventListener('focus', handleFocus);
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, []);

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg?.waiting) {
          // Tell the waiting SW to skip waiting → becomes active
          reg.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
        // Send clear cache message to active SW
        if (reg?.active) {
          reg.active.postMessage({ type: 'CLEAR_CACHE' });
        }
      }
      // Force reload after a short delay (let SW process messages)
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch {
      // Fallback: just reload
      window.location.reload();
    }
  };

  const handleDismiss = () => {
    setUpdateAvailable(false);
  };

  // Show toast when update is available
  useEffect(() => {
    if (!updateAvailable || updating) return;

    const toastId = toast('Nueva versión disponible', {
      description: 'Hemos mejorado Profileia. Actualiza para tener la última versión.',
      duration: Infinity, // Stay until user acts
      action: {
        label: 'Actualizar',
        onClick: () => handleUpdate(),
      },
      cancel: {
        label: 'Ahora no',
        onClick: () => handleDismiss(),
      },
      icon: <RefreshCw className="size-4 text-primary" />,
    });

    return () => {
      toast.dismiss(toastId);
    };
  }, [updateAvailable, updating]);

  return null; // Renders nothing — uses toast for the UI
}
